<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form v-model="formData['valid']" class="mb-3" @submit.prevent="updateProductSKU">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Update Product SKU</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['product_name']" autofocus
                                          :rules="[vRules.required, vRules.maxLength(64)]" density="compact"
                                          label="Product Name"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['sku']" :rules="[vRules.required, vRules.maxLength(32)]"
                                          density="compact" label="SKU"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['portals']" :items="portals" :loading="!portals.length"
                                      :rules="[vRules.required]" density="compact" item-title="name" item-value="id"
                                      chips label="Portals" multiple/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['master_configs']" :items="masterConfigs"
                                      :loading="!masterConfigs.length" :rules="[vRules.required]" chips
                                      density="compact" item-title="name" item-value="id" label="Master Config Files"
                                      multiple/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['diagnostic_profile_id']" :items="profiles"
                                      density="compact" item-title="name" item-value="id"
                                      label="Select Diagnostic Profile"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['subscription_plan_id']" :items="plans"
                                      :rules="[vRules.required]" density="compact" item-title="name" item-value="id"
                                      label="Default Subscription Plan">
                            </v-select>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['product_type']" :items="productTypes"
                                      :rules="[vRules.required]" density="compact" item-title="name" item-value="value"
                                      label="Product Type"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['primary_battery_type']" :items="batteryTypes"
                                      :rules="[vRules.required]" density="compact" item-title="name" item-value="value"
                                      label="Primary Battery Type"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-file-input v-model="formData['picture']" density="compact" label="Device Picture"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['hw_revision']"
                                          :rules="[vRules.required, vRules.maxLength(32)]" density="compact"
                                          label="HW Revision"/>
                        </v-col>

                        <v-col cols="12" sm="12">
                            <v-text-field v-model="formData['manufacture_name']"
                                          :rules="[vRules.required, vRules.maxLength(64)]"
                                          density="compact" label="Manufacture Name"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['manufacture_code']"
                                          :rules="[vRules.required, vRules.maxLength(32)]"
                                          density="compact" label="Manufacture Code"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['fcc_code']"
                                          :rules="[vRules.required, vRules.maxLength(32)]"
                                          density="compact" label="FCC Code"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['latest_firmware_1']"
                                          :rules="[vRules.required, vRules.maxLength(32)]"
                                          density="compact" label="Latest Firmware 1"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['latest_firmware_2']"
                                          :rules="[vRules.required, vRules.maxLength(32)]"
                                          density="compact" label="Latest Firmware 2"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['country_of_origin']"
                                          :rules="[vRules.required, vRules.maxLength(32)]"
                                          density="compact" label="Country of Origin"/>
                        </v-col>

                        <v-col cols="12">
                            <h4>Battery Channels</h4>
                            <div class="text-caption text-medium-emphasis">
                                The tracker always reports two voltage channels, voltage1 and voltage2. What
                                you set here is how the backend reads them: what each channel is, and which
                                one gets shown to users and used for battery alerts.
                            </div>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['battery1_fully_charge_threshold']"
                                          :rules="[vRules.required]" density="compact" suffix="V"
                                          label="Battery 1 Fully Charge Threshold" type="number"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['battery1_low_charge_threshold']"
                                          :rules="[vRules.required]" density="compact" suffix="V"
                                          label="Battery 1 Low Charge Threshold" type="number"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['battery1_critical_charge_threshold']"
                                          :rules="[vRules.required]" density="compact" suffix="V"
                                          label="Battery 1 Critical Charge Threshold" type="number"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['battery2_fully_charge_threshold']"
                                          :rules="[vRules.required]" density="compact" suffix="V"
                                          label="Battery 2 Fully Charge Threshold" type="number"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['battery2_low_charge_threshold']"
                                          :rules="[vRules.required]" density="compact" suffix="V"
                                          label="Battery 2 Low Charge Threshold" type="number"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['battery2_critical_charge_threshold']"
                                          :rules="[vRules.required]" density="compact" suffix="V"
                                          label="Battery 2 Critical Charge Threshold" type="number"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['battery1_label']" density="compact"
                                          label="Battery 1 Label" placeholder="e.g. Super Cap"
                                          :hint="battery1Hint" maxlength="64"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['battery2_label']" density="compact"
                                          label="Battery 2 Label" placeholder="e.g. External Power"
                                          :hint="battery2Hint" maxlength="64"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['battery1_type']" :items="powerSourceTypes"
                                      clearable density="compact" item-title="name" item-value="value"
                                      label="Battery 1 Power Source Type"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['battery2_type']" :items="powerSourceTypes"
                                      clearable density="compact" item-title="name" item-value="value"
                                      label="Battery 2 Power Source Type"/>
                        </v-col>
                        <v-col cols="12">
                            <v-radio-group v-model="batteryDisplayChannel" hide-details inline
                                           label="Which battery to display">
                                <v-radio :label="channel1Label" :value="1"/>
                                <v-radio :label="channel2Label" :value="2"/>
                            </v-radio-group>
                            <div class="text-caption text-medium-emphasis">
                                Selects the voltage channel shown in the live view, the device list and the
                                low battery alert. Exactly one channel applies; the other is still recorded
                                but never surfaced.
                            </div>
                            <v-alert v-if="batteryChannelWarning" class="mt-3" density="compact"
                                     type="warning" variant="tonal">
                                {{ batteryChannelWarning }}
                            </v-alert>
                            <v-alert v-if="thresholdWarnings.length" class="mt-3" density="compact"
                                     type="warning" variant="tonal">
                                <p v-for="warning in thresholdWarnings" :key="warning">{{ warning }}</p>
                            </v-alert>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <h4>Features Supported</h4>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['wifi']" color="primary" label="Wifi"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['cellular']" color="primary" label="Cellular"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['ble']" color="primary" label="BLE"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['lora']" color="primary" label="LORA"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['ethernet']" color="primary" label="Ethernet"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['gateway']" color="primary" label="Gateway"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['gps']" color="primary" label="GPS"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['beacon']" color="primary" label="Beacon"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['external_sim1']" color="primary" label="External SIM1"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['external_sim2']" color="primary" label="External SIM2"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['internal_sim2']" color="primary" label="Internal SIM2"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['rechargeable']" color="primary" label="Rechargeable"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['super_cap']" color="primary" label="Super Cap"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['external_power']" color="primary" label="External Power"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['temp_sensor']" color="primary" label="Temp Sensor"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['humidity_sensor']" color="primary" label="Humidity Sensor"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['light_sensor']" color="primary" label="Light Sensor"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['solar']" color="primary" label="Solar"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['tamper_detection']" color="primary" label="Tamper Detection"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['nfc']" color="primary" label="NFC"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['magnet_switch']" color="primary" label="Magnet Switch"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['push_button']" color="primary" label="Push Button"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['cpuid_1']" color="primary" label="CPU ID 1"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['cpuid_2']" color="primary" label="CPU ID 2"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['obd_present']" color="primary" label="OBD Present"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['carb_capable']" color="primary" label="CARB Capable"/>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {computed, onMounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useSkuBatteryChannel} from "@/composables/useSkuBatteryChannel";
import {useSnackbarStore} from "@/stores/snackBarStore";

const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    sku: {
        type: Object
    }
});

const productTypes = [
    {
        value: 0,
        name: 'Tracker'
    },
    {
        value: 1,
        name: 'Beacon'
    },
    {
        value: 2,
        name: 'Gateway'
    },
    {
        value: 3,
        name: 'Location Assistant'
    },
    {
        value: 4,
        name: 'Driver Tag'
    },
    {
        value: 5,
        name: 'Solar MPPT Controller'
    }
];

const batteryTypes = [
    {value: 1, name: 'Alkaline'},
    {value: 2, name: 'Lithium Ion'},
    {value: 3, name: 'Lithium Polymer'},
    {value: 4, name: 'Nickel Metal Hydride'},
    {value: 5, name: 'Nickel Cadmium'},
    {value: 6, name: 'Lead Acid'},
    {value: 7, name: 'Zinc Carbon'},
    {value: 8, name: 'Silver Oxide'},
    {value: 9, name: 'Zinc Air'},
    {value: 10, name: 'Rechargeable Alkaline'},
    {value: 11, name: 'Primary Lithium'},
    {value: 12, name: 'Rechargeable Lithium'},
    {value: 13, name: 'Lithium Iron Phosphate'},
    {value: 14, name: 'Lithium Manganese Dioxide'},
    {value: 15, name: 'LNCAO (Lithium Nickel Cobalt)'},
    {value: 16, name: 'LNMCO (Lithium Nickel Manganese)'},
    {value: 17, name: 'Lithium Cobalt Oxide'},
    {value: 18, name: 'Lithium Titanate'},
    {value: 19, name: 'Sodium Ion'},
    {value: 20, name: 'Solid State'},
    {value: 21, name: 'Flow'},
    {value: 22, name: 'Thin Film'},
    {value: 23, name: 'Molten Salt'},
    {value: 24, name: 'Magnesium Ion'},
    {value: 25, name: 'Graphene'},
    {value: 26, name: 'Organic'},
    {value: 27, name: 'Zinc Manganese Dioxide'},
    {value: 28, name: 'Nickel Zinc'},
    {value: 29, name: 'Silver Zinc'},
    {value: 30, name: 'Dual Carbon'},
];

const powerSourceTypes = [
    {value: 0, name: 'Replaceable Non-Rechargeable Battery'},
    {value: 1, name: 'Non-Replaceable Non-Rechargeable Battery'},
    {value: 2, name: 'Non-Replaceable Rechargeable Battery'},
    {value: 3, name: 'External Power'},
    {value: 4, name: 'Super Capacitor'},
];

const attachedPortals = computed(() => {
    if (props.sku.portals && props.sku.portals.length) {
        return props.sku.portals.map((portal) => portal.id)
    }
    return null
});

const attachedMasterConfigs = computed(() => {
    if (props.sku.master_configs && props.sku.master_configs.length) {
        return props.sku.master_configs.map((config) => config.id)
    }
    return null
});

const formData = reactive({
    valid: false,
    _method: 'put',
    portals: attachedPortals.value,
    master_configs: attachedMasterConfigs.value,
    subscription_plan_id: props.sku.subscription_plan_id ? parseInt(props.sku.subscription_plan_id) : null,
    diagnostic_profile_id: props.sku.diagnostic_profile_id ? parseInt(props.sku.diagnostic_profile_id) : null,
    product_name: props.sku.product_name,
    sku: props.sku.sku,
    picture: null,
    hw_revision: props.sku.hw_revision,
    manufacture_code: props.sku.manufacture_code,
    manufacture_name: props.sku.manufacture_name,
    product_type: props.sku.product_type,
    primary_battery_type: props.sku.primary_battery_type,
    fcc_code: props.sku.fcc_code,
    latest_firmware_1: props.sku.latest_firmware_1,
    latest_firmware_2: props.sku.latest_firmware_2,
    country_of_origin: props.sku.country_of_origin,
    wifi: !!props.sku.wifi,
    cellular: !!props.sku.cellular,
    ble: !!props.sku.ble,
    lora: !!props.sku.lora,
    ethernet: !!props.sku.ethernet,
    gateway: !!props.sku.gateway,
    gps: !!props.sku.gps,
    beacon: !!props.sku.beacon,
    external_sim1: !!props.sku.external_sim1,
    external_sim2: !!props.sku.external_sim2,
    internal_sim2: !!props.sku.internal_sim2,
    rechargeable: !!props.sku.rechargeable,
    super_cap: !!props.sku.super_cap,
    external_power: !!props.sku.external_power,
    temp_sensor: !!props.sku.temp_sensor,
    humidity_sensor: !!props.sku.humidity_sensor,
    solar: !!props.sku.solar,
    tamper_detection: !!props.sku.tamper_detection,
    nfc: !!props.sku.nfc,
    magnet_switch: !!props.sku.magnet_switch,
    light_sensor: !!props.sku.light_sensor,
    push_button: !!props.sku.push_button,
    cpuid_1: !!props.sku.cpuid_1,
    cpuid_2: !!props.sku.cpuid_2,
    obd_present: !!props.sku.obd_present,
    carb_capable: !!props.sku.carb_capable,
    battery1_status: !!props.sku.battery1_status,
    battery2_status: !!props.sku.battery2_status,
    battery1_fully_charge_threshold: props.sku.battery1_fully_charge_threshold,
    battery1_low_charge_threshold: props.sku.battery1_low_charge_threshold,
    battery1_critical_charge_threshold: props.sku.battery1_critical_charge_threshold,
    battery2_fully_charge_threshold: props.sku.battery2_fully_charge_threshold,
    battery2_low_charge_threshold: props.sku.battery2_low_charge_threshold,
    battery2_critical_charge_threshold: props.sku.battery2_critical_charge_threshold,
    battery1_label: props.sku.battery1_label ?? null,
    battery2_label: props.sku.battery2_label ?? null,
    battery1_type: props.sku.battery1_type ?? null,
    battery2_type: props.sku.battery2_type ?? null,
});

const {
    channel1Label,
    channel2Label,
    batteryDisplayChannel,
    batteryChannelWarning,
    thresholdWarnings,
    battery1Hint,
    battery2Hint,
} = useSkuBatteryChannel(formData);

const emits = defineEmits(['update:modelValue', 'updateSku']);
const dialogSwitch = ref(props.modelValue);
const masterConfigs = ref([]);
const profiles = ref([]);
const portals = ref([]);
const errors = ref([]);
const plans = ref([]);

onMounted(() => {
    fetchPlans()
    fetchPortals()
    fetchProfiles()
    fetchMasterConfigs()
})
const fetchProfiles = () => {
    send('get', `admin/diagnostics/profiles?list=true`)
        .then((response) => {
            profiles.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Diagnostic Profiles List could not be loaded', 'error')
        })
}
const fetchPlans = () => {
    send('get', `subscription-plans/list`)
        .then((response) => {
            plans.value = response.data.data;
        }).catch((error) => {
        snackbar.showSnackbar('Subscription plans List could not be loaded', 'error')
    })
}
const fetchPortals = () => {
    send('get', `admin/portals`)
        .then((response) => {
            portals.value = response.data.data;
        }).catch((error) => {
        snackbar.showSnackbar('Portal List could not be loaded', 'error')
    })
}
const fetchMasterConfigs = () => {
    send('post', `admin/device-configs-list?master=1`)
        .then((response) => {
            masterConfigs.value = response.data.data;
        }).catch((error) => {
        snackbar.showSnackbar('Master configs files List could not be loaded', 'error')
    })
}
const updateProductSKU = () => {
    if (formData.valid) {
        errors.value = [];
        if (formData['picture']) {
            formData['picture'] = formData['picture'];
        }
        send('post', `product-skus/${props.sku.id}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }).then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits('updateSku', response.data.data.product_sku);
                emits('update:modelValue', false);
            }
        }).catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
    }
}

</script>
